import { ConfirmModal, CustomButton } from '@/shared/ui/components';
import { editIcon } from '@/shared/ui/assets';
import { ReactSVG } from 'react-svg';
import { t } from 'ttag';
import { getContent } from '@/entities/study';
import GroupData from '../studyDetailsTab/GroupData';
import ManagedEditLock from '../managedEditLock/managedEditLock';
import { usePatientDetailsTab } from './usePatientDetailsTab';
import { PATIENT_DETAIL_KEY } from '../../config/patientDetailsTab';
import {
  generateInputList,
  generateInsurance,
  generateMedicalHistory,
  generatePatientContact,
  generatePatientInfo,
  generateSecondaryContact,
} from '../../lib/patientDetailsTab';
import EditPatientDetail from './editPatientDetails/EditPatientDetail';
import type { PatientDetailsTabProps } from './patientDetailsTab.types';
import './style.scss';

/**
 * Placeholder callback for patient detail props that require a function but no behavior. Takes no arguments and performs nothing when invoked.
 *
 * @returns Nothing.
 */
const NOOP = () => {};
const EMPTY_OBJECT = {};

const PatientDetailsTab = ({
  isActive,
  isLoadedData,
  data = EMPTY_OBJECT,
  countries,
  updateStudyDetail = NOOP,
  isAbortedStudy,
  isAbortingStudy,
  isC1cProviderStudy = false,
}: PatientDetailsTabProps) => {
  const { state, setState, onSave, handleOpenDrawer, handleClose, handleClick } =
    usePatientDetailsTab({ data, updateStudyDetail });

  if (!isActive) {
    return null;
  }

  /**
   * Renders a patient detail section heading together with an edit button that opens the drawer for that section's key. The button is disabled while the study is aborted or aborting, or when CardioOne manages it. An aborted study explains through a tooltip that no actions are possible; a managed one points to the external portal.
   *
   * @param title - Section heading text.
   * @param key - Patient section identifier passed to the drawer opener.
   * @returns The section title with its edit button.
   */
  const renderTitle = ({ title, editLabel, key }: any) => (
    <div className="editable-title">
      {title}
      <ManagedEditLock isLocked={isC1cProviderStudy}>
        <CustomButton
          id="edit-patient-contact-btn"
          size="small"
          type="secondary"
          style="default"
          icon={<ReactSVG src={editIcon} />}
          disabled={isAbortedStudy || isAbortingStudy || isC1cProviderStudy}
          tooltipTitle={
            isAbortedStudy
              ? t`The study has been aborted. You cannot perform any actions.`
              : undefined
          }
          tooltipPlacement="bottomLeft"
          ariaLabel={editLabel}
          onClick={() => {
            handleOpenDrawer(key);
          }}
        />
      </ManagedEditLock>
    </div>
  );

  return (
    <>
      <ConfirmModal
        title={t`Apply changes`}
        question={getContent()}
        isOpen={state.isOpenConfirmModal}
        leftButtonName={t`Cancel`}
        rightButtonName={t`Apply changes`}
        onClickLeftButton={() => {
          setState({
            payload: null,
            isOpenConfirmModal: false,
          });
        }}
        onClickRightButton={() => {
          onSave(state.payload, state.edittingKey, state.couldCheckReportSize);
        }}
        isSaving={state.isSaving}
      />

      <div className="patient-detail-tab animated fadeIn">
        <EditPatientDetail
          isOpenModal={state.isOpenModal}
          content={generateInputList({
            title: state.isOpenModal,
            data,
            handleClick,
            countries,
            isSaving: state.isSaving,
          })}
          handleClose={handleClose}
        />
        <div className="multiple-section">
          <GroupData
            className="left-section"
            isLoadedData={isLoadedData}
            title={renderTitle({
              title: t`Patient information`,
              editLabel: t`Edit patient information`,
              key: PATIENT_DETAIL_KEY.PATIENT_INFORMATION,
            })}
            content={generatePatientInfo(data)}
          />
          <div className="right-section">
            <GroupData
              isLoadedData={isLoadedData}
              title={renderTitle({
                title: t`Patient contact`,
                editLabel: t`Edit patient contact`,
                key: PATIENT_DETAIL_KEY.PATIENT_CONTACT,
              })}
              content={generatePatientContact(data)}
            />
            <GroupData
              isLoadedData={isLoadedData}
              title={renderTitle({
                title: t`Secondary contact`,
                editLabel: t`Edit secondary contact`,
                key: PATIENT_DETAIL_KEY.SECONDARY_CONTACT,
              })}
              content={generateSecondaryContact(data)}
            />
          </div>
        </div>
        <div className="multiple-section">
          <GroupData
            className="left-section"
            isLoadedData={isLoadedData}
            title={renderTitle({
              title: t`Insurance`,
              editLabel: t`Edit insurance`,
              key: PATIENT_DETAIL_KEY.INSURANCE,
            })}
            content={generateInsurance(data)}
          />
          <GroupData
            className="right-section gap-0"
            isLoadedData={isLoadedData}
            title={renderTitle({
              title: t`Medical history`,
              editLabel: t`Edit medical history`,
              key: PATIENT_DETAIL_KEY.MEDICAL_HISTORY,
            })}
            content={generateMedicalHistory(data)}
          />
        </div>
      </div>
    </>
  );
};

export default PatientDetailsTab;
